import * as React from 'react';

import {
  isAuthenticationResponse,
  isInvalidCredentialsError,
  isOrganizationSelectionError,
} from '@rematter/frontend-lib-api-client';
import { Button, OTPField } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom } from 'jotai';

import { $signIn, OrganizationRecord } from '../state/signIn.state';

import { useTranslations } from '~/app/i18n/useTranslations';
import { config } from '~/config';
import { useApi, useIdentity } from '~/shared/hooks';

export const Totp = () => {
  const toast = useToast();
  const t = useTranslations();
  const [signInState, setSignInState] = useAtom($signIn);
  const [verificationCode, setVerificationCode] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { setAccessToken, setOrganization, setUser } = useIdentity();
  const { client } = useApi();

  const handleVerification = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (
      !signInState.pendingAuthenticationToken ||
      !signInState.mfa?.challengeId ||
      verificationCode.length !== 6
    )
      return;

    setIsSubmitting(true);

    try {
      const { authenticationVerifyTotp } = await client(
        'mutation.authentication.verifyTotp',
      ).mutation({
        authenticationVerifyTotp: {
          __typename: true,
          __args: {
            clientId: config.workOs.clientId ?? '',
            pendingAuthenticationToken: signInState.pendingAuthenticationToken,
            code: verificationCode,
            authenticationChallengeId: signInState.mfa.challengeId,
          },
          on_OrganizationSelectionError: {
            pendingAuthenticationToken: true,
            organizations: {
              id: true,
              name: true,
            },
          },
          on_AuthenticationResponse: {
            accessToken: true,
          },
        },
      });

      if (isAuthenticationResponse(authenticationVerifyTotp)) {
        setSignInState({ ...signInState, authenticated: true });
        setAccessToken(authenticationVerifyTotp.accessToken);
        setUser(authenticationVerifyTotp.user);
        setOrganization(authenticationVerifyTotp.organization);
        return;
      }

      if (isOrganizationSelectionError(authenticationVerifyTotp)) {
        setSignInState({
          ...signInState,
          step: 'org-selection',
          organizations:
            authenticationVerifyTotp.organizations as OrganizationRecord[],
          pendingAuthenticationToken:
            authenticationVerifyTotp.pendingAuthenticationToken,
        });
        return;
      }

      if (isInvalidCredentialsError(authenticationVerifyTotp)) {
        toast.error(t.totp.errors.invalid);
      }
    } catch (err) {
      toast.error(String(err));
      // Reset to credentials step on error
      setSignInState({
        step: 'credentials',
        authenticated: false,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add a handler specifically for the button press event
  const handleButtonPress = () => {
    handleVerification();
  };

  const handleBack = () => {
    setSignInState({
      ...signInState,
      step: 'credentials',
      pendingAuthenticationToken: undefined,
      mfa: undefined,
    });
  };

  return (
    <form onSubmit={handleVerification} className="@Totp flex flex-col gap-5">
      <div className="flex flex-col gap-6">
        <h2 className="text-center text-xl font-semibold text-neutral/80">
          {t.totp.title}
        </h2>

        <p className="text-center text-neutral">{t.totp.subtitle}</p>

        {signInState.step === 'mfa-enroll' && signInState.mfa?.qrCode && (
          <div className="flex flex-col items-center gap-4">
            <img
              src={signInState.mfa.qrCode}
              alt="QR Code for MFA setup"
              className="size-48"
            />
            <p className="text-center text-sm text-neutral">
              {t.totp.qrSetup.title}
            </p>
          </div>
        )}

        <OTPField
          onChange={setVerificationCode}
          isDisabled={isSubmitting}
          autoFocus={true}
          length={6}
        />

        <div className="flex flex-col gap-3">
          <Button
            onPress={handleButtonPress}
            isDisabled={isSubmitting || verificationCode.length !== 6}
            className="w-full"
          >
            {t.totp.submit.verify}
          </Button>

          <Button
            onPress={handleBack}
            isDisabled={isSubmitting}
            className="w-full"
          >
            {t.totp.goBack}
          </Button>
        </div>
      </div>
    </form>
  );
};
